import { useState, useEffect, useRef, useMemo } from 'react';
import { Search, User, ChevronDown, Check, Package } from 'lucide-react';
import { Input } from '@/components/ui/input';

/**
 * Searchable Patient Dropdown Component
 * Combines search input with dropdown selection
 */
export const PatientSearchDropdown = ({ 
  patients = [], 
  selectedPatientId, 
  onSelect, 
  placeholder = "Search patient by name, ID, or phone...",
  required = false,
  disabled = false,
  className = ""
}) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Filter patients based on search
  const filteredPatients = useMemo(() => {
    if (!search.trim()) return patients.slice(0, 10);
    const query = search.toLowerCase();
    return patients.filter(p => 
      p.full_name?.toLowerCase().includes(query) ||
      p.patient_number?.toLowerCase().includes(query) ||
      p.phone?.includes(query)
    ).slice(0, 10);
  }, [patients, search]);

  // Get selected patient for display
  const selectedPatient = useMemo(() => {
    return patients.find(p => p.id === selectedPatientId);
  }, [patients, selectedPatientId]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div 
        className={`flex items-center border rounded-md transition-colors ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'cursor-pointer hover:border-gray-400'}`}
      >
        <Search className="absolute left-3 h-4 w-4 text-gray-400 pointer-events-none" />
        <Input
          placeholder={placeholder}
          value={isOpen ? search : (selectedPatient ? `${selectedPatient.full_name} (${selectedPatient.patient_number})` : '')}
          onChange={(e) => {
            setSearch(e.target.value);
            setIsOpen(true);
          }}
          onClick={() => !disabled && setIsOpen(true)}
          className="pl-10 pr-10 border-0 focus-visible:ring-0 cursor-pointer"
          disabled={disabled}
          data-testid="patient-search-dropdown"
        />
        <ChevronDown 
          className={`absolute right-3 h-4 w-4 text-gray-400 transition-transform cursor-pointer ${isOpen ? 'rotate-180' : ''}`} 
          onClick={() => !disabled && setIsOpen(!isOpen)}
        />
      </div>
      
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-lg border border-gray-200 shadow-lg max-h-60 overflow-y-auto">
          {filteredPatients.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500 text-center">
              No patients found
            </div>
          ) : (
            filteredPatients.map((patient) => (
              <button
                key={patient.id}
                type="button"
                className={`w-full px-4 py-3 text-left hover:bg-purple-50 border-b last:border-b-0 flex items-center justify-between transition-colors ${
                  selectedPatientId === patient.id ? 'bg-purple-50' : ''
                }`}
                onClick={() => {
                  onSelect(patient.id);
                  setSearch('');
                  setIsOpen(false);
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-purple-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">{patient.full_name}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {patient.patient_number} {patient.phone && `• ${patient.phone}`}
                    </p>
                  </div>
                </div>
                {selectedPatientId === patient.id && (
                  <Check className="w-4 h-4 text-purple-600 flex-shrink-0" />
                )}
              </button>
            ))
          )}
        </div>
      )}
      {required && <input type="hidden" value={selectedPatientId || ''} required />}
    </div>
  );
};

/**
 * Searchable Fixed Charge Dropdown Component
 * For selecting from fixed charge master list
 */
export const FixedChargeSearchDropdown = ({
  charges = [],
  onSelect,
  placeholder = "Search fixed charges (e.g., Room Rent, Nursing Care)...",
  disabled = false,
  className = ""
}) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Filter charges based on search
  const filteredCharges = useMemo(() => {
    if (!search.trim()) return charges.slice(0, 10);
    const query = search.toLowerCase();
    return charges.filter(c => 
      c.charge_name?.toLowerCase().includes(query) ||
      c.charge_code?.toLowerCase().includes(query) ||
      c.category?.toLowerCase().includes(query) ||
      c.description?.toLowerCase().includes(query)
    ).slice(0, 10);
  }, [charges, search]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getCategoryColor = (category) => {
    const colors = {
      'consultation': 'bg-blue-100 text-blue-700',
      'procedure': 'bg-green-100 text-green-700',
      'lab': 'bg-purple-100 text-purple-700',
      'imaging': 'bg-orange-100 text-orange-700',
      'pharmacy': 'bg-pink-100 text-pink-700',
      'room': 'bg-indigo-100 text-indigo-700',
      'nursing': 'bg-teal-100 text-teal-700',
      'equipment': 'bg-yellow-100 text-yellow-700',
      'miscellaneous': 'bg-gray-100 text-gray-700'
    };
    return colors[category?.toLowerCase()] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div className={`flex items-center border rounded-md transition-colors ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}>
        <Search className="absolute left-3 h-4 w-4 text-gray-400 pointer-events-none" />
        <Input
          placeholder={placeholder}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setIsOpen(true);
          }}
          onClick={() => !disabled && setIsOpen(true)}
          onFocus={() => !disabled && setIsOpen(true)}
          className="pl-10 pr-10 border-0 focus-visible:ring-0 cursor-pointer"
          disabled={disabled}
          data-testid="fixed-charge-search-dropdown"
        />
        <ChevronDown 
          className={`absolute right-3 h-4 w-4 text-gray-400 transition-transform cursor-pointer ${isOpen ? 'rotate-180' : ''}`} 
          onClick={() => !disabled && setIsOpen(!isOpen)}
        />
      </div>
      
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-lg border border-gray-200 shadow-lg max-h-72 overflow-y-auto">
          {filteredCharges.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500 text-center">
              {search ? 'No charges found' : 'No fixed charges available'}
            </div>
          ) : (
            filteredCharges.map((charge) => (
              <button
                key={charge.id}
                type="button"
                className="w-full px-4 py-3 text-left hover:bg-purple-50 border-b last:border-b-0 transition-colors"
                onClick={() => {
                  onSelect(charge);
                  setSearch('');
                  setIsOpen(false);
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Package className="w-4 h-4 text-purple-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 truncate">{charge.charge_name}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {charge.charge_code} {charge.description && `• ${charge.description}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getCategoryColor(charge.category)}`}>
                      {charge.category}
                    </span>
                    <span className="font-semibold text-gray-900">₹{charge.rate?.toFixed(2)}</span>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default { PatientSearchDropdown, FixedChargeSearchDropdown };
